import type { FeatureModule, FeatureState } from '../types';

type CursorSize = '48' | '64';

/**
 * Generate a white arrow cursor SVG with black border as a data URI.
 * The SVG is designed to be clearly visible on any background.
 */
function createCursorSvg(size: number): string {
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 ${size} ${size}">
    <defs>
      <filter id="shadow" x="-20%" y="-20%" width="140%" height="140%">
        <feDropShadow dx="1" dy="1" stdDeviation="1" flood-color="#000" flood-opacity="0.5"/>
      </filter>
    </defs>
    <g filter="url(#shadow)">
      <path d="M ${size * 0.15} ${size * 0.05} L ${size * 0.15} ${size * 0.75} L ${size * 0.35} ${size * 0.6} L ${size * 0.55} ${size * 0.9} L ${size * 0.65} ${size * 0.85} L ${size * 0.45} ${size * 0.55} L ${size * 0.7} ${size * 0.5} Z"
            fill="white" stroke="black" stroke-width="${size * 0.04}" stroke-linejoin="round"/>
    </g>
  </svg>`;
  return `data:image/svg+xml;base64,${btoa(svg)}`;
}

export default function createBigCursorModule(): FeatureModule {
  let enabled = false;
  let currentSize: CursorSize = '48';
  const STYLE_ID = 'accessify-big-cursor';
  const STORAGE_KEY = 'accessify-big-cursor';

  // Pre-generate cursor data URIs
  const cursors: Record<CursorSize, string> = {
    '48': createCursorSvg(48),
    '64': createCursorSvg(64),
  };

  function getStyles(size: CursorSize): string {
    const numSize = parseInt(size, 10);
    const hotspot = Math.round(numSize * 0.15);
    return `
      /* accessify big cursor */
      html, html * {
        cursor: url('${cursors[size]}') ${hotspot} ${hotspot}, auto !important;
      }
      html a, html a *, html button, html button *,
      html input[type="submit"], html input[type="button"],
      html [role="button"], html [role="button"] * {
        cursor: url('${cursors[size]}') ${hotspot} ${hotspot}, pointer !important;
      }
    `;
  }

  function injectStyles(size: CursorSize) {
    let styleEl = document.getElementById(STYLE_ID);
    if (!styleEl) {
      styleEl = document.createElement('style');
      styleEl.id = STYLE_ID;
      document.head.appendChild(styleEl);
    }
    styleEl.textContent = getStyles(size);
  }

  function removeStyles() {
    const styleEl = document.getElementById(STYLE_ID);
    styleEl?.remove();
  }

  function activate() {
    enabled = true;
    const saved = localStorage.getItem(STORAGE_KEY) as CursorSize | null;
    currentSize = saved === '64' ? '64' : '48';
    injectStyles(currentSize);
  }

  function deactivate() {
    enabled = false;
    removeStyles();
    localStorage.removeItem(STORAGE_KEY);
  }

  return {
    id: 'big-cursor',
    name: () => 'Big Cursor',
    description: 'Enlarge the mouse cursor for easier visibility',
    icon: 'big-cursor',
    category: 'motor',
    activate,
    deactivate,
    getState: (): FeatureState => ({
      id: 'big-cursor',
      enabled,
      value: currentSize,
    }),
    setState: (size: CursorSize) => {
      currentSize = size === '64' ? '64' : '48';
      if (enabled) {
        injectStyles(currentSize);
      }
      localStorage.setItem(STORAGE_KEY, currentSize);
    },
  };
}
